import { getProfilePhoto } from '@api/profile'
import {
  getAllAccounts,
  getNewEligibleWaterAccount,
  getWaterAccounts,
} from '@api/waterAccount'
import { Hidden, Visible } from '@components/Common'
import { ExpiringPopup } from '@components/ExpiringPopup'
import { useGlobalLoading } from '@hooks/useGlobalLoading'
import { WaterAccountManagement } from '@models/waterAccount.model'
import { usePubDispatch, usePubSelector } from '@redux/hook'
import { isAuthenticatedSelected } from '@redux/selectors'
import { setPhoto } from '@redux/slices/authSlice'
import {
  setAccountList,
  setEligibleWA,
  setTerminatedWA,
  setWaterAccountDetails,
  updateWaterAccountState,
} from '@redux/slices/waterAccountSlice'
import { Masthead, Navbar, NavbarMobile } from '@src/layouts'
import { ROUTE_PATHS } from '@src/routes'
import { Helpers } from '@utils/helper'
import { LodashUtils } from '@utils/lodash'
import { useEffect } from 'react'
import { Navigate, useLocation, useNavigate, useOutlet } from 'react-router-dom'
import './style.css'

export default function Portal() {
  const outlet = useOutlet()
  const navigate = useNavigate()
  const dispatch = usePubDispatch()
  const location = useLocation()
  const loading = useGlobalLoading()

  const authenticated = usePubSelector(isAuthenticatedSelected)
  const accountSelected = usePubSelector(
    (states) => states.waterAccount.accountSelected,
  )

  const preloadData = async () => {
    const loadingId = loading.present({ title: 'Initializing...' })
    try {
      const [accountsResp, profilePhotoResp, eligibleResp] =
        await Promise.allSettled([
          getAllAccounts(),
          getProfilePhoto(),
          getNewEligibleWaterAccount(),
        ])
      const waterAccounts: WaterAccountManagement.WaterAccountModel[] =
        LodashUtils.get(accountsResp, 'value.data', [])
      dispatch(
        setTerminatedWA(
          waterAccounts?.filter((wa: any) => wa.validationStatus === 2) as any,
        ),
      )
      const eligibleList: any = LodashUtils.get(
        eligibleResp,
        'value.data.ownerEligible',
        [],
      )
      dispatch(setEligibleWA(eligibleList))
      const base64String: any = LodashUtils.get(
        profilePhotoResp,
        'value.data',
        null,
      )

      const sorted = LodashUtils.sortBy(waterAccounts, 'sortOrder')
      const isMerged = Helpers.checkIsMergedAccount(sorted[0])
      const accounts = sorted.map((o) => o.accountNumber)
      const resp = await getWaterAccounts(accounts)
      const accountDetails = LodashUtils.get(resp, 'data.waterAccounts', [])
      dispatch(setWaterAccountDetails(accountDetails))

      if (location.pathname === ROUTE_PATHS.LEAKS) {
        dispatch(setAccountList(sorted))
      } else {
        const _accountSelected = LodashUtils.find(sorted, (o) => {
          return (
            `${o?.mergedWaterAccountId || ''}${o?.accountNumber || ''}` ===
            `${accountSelected?.mergedId || ''}${
              accountSelected?.contractNumber || ''
            }`
          )
        })

        const selected = {
          isMerged,
          mergedId: sorted[0].mergedWaterAccountId,
          contractNumber: sorted[0].accountNumber,
        }

        dispatch(
          updateWaterAccountState({
            accountList: sorted,
            accountSelected: Object.keys(_accountSelected || {}).length
              ? accountSelected
              : selected,
          }),
        )
      }

      dispatch(
        setPhoto(base64String ? `data:image/jpeg;base64, ${base64String}` : ''),
      )
      if (location.pathname === ROUTE_PATHS.PORTAL) {
        navigate(ROUTE_PATHS.DASHBOARD)
      }
    } finally {
      loading.dismiss(loadingId)
    }
  }

  useEffect(() => {
    if (authenticated) {
      preloadData()
    }
  }, [authenticated])

  if (!authenticated) {
    return <Navigate to={ROUTE_PATHS.LOGIN} />
  }

  return (
    <>
      <Hidden xs md sm base>
        <Masthead />
        <Navbar />
      </Hidden>
      {outlet}
      <Visible xs md sm>
        <NavbarMobile />
      </Visible>
      <ExpiringPopup />
    </>
  )
}
